import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isPrestataireLibreRole } from '../../utils/roleUtils';
import { ReactComponent as LogoIcon } from "../../assets/logo/logo-centralis-gris.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/view-more.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/visibility-off.svg";
import Button from '../../composants/Button/Button';
import './Login.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Récupérer le message d'erreur passé via la navigation (ex: déconnexion forcée)
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      // Nettoyer le state pour éviter de réafficher l'erreur après un refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);

      // Récupérer les informations utilisateur du localStorage
      const userDataString = localStorage.getItem('user');
      const userData = userDataString ? JSON.parse(userDataString) : {};
      const userRole = userData.role || '';

      // Verifier si l'utilisateur est un prestataire libre
      if (isPrestataireLibreRole(userRole)) {
        navigate('/profil');
        return;
      }

      // Vérifier si l'utilisateur a un rôle d'acheteur
      const isAcheteur =
        userRole === "Acheteur" ||
        userRole === "Acheteur signataire" ||
        userRole === "Acheteur signataire admin" ||
        userRole.toLowerCase().includes('acheteur');

      // Rediriger vers la page appropriée selon le rôle
      navigate(isAcheteur ? '/operations' : '/commandes');

    } catch (error) {
      // Récupérer le message d'erreur précis de l'API
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Une erreur est survenue lors de la connexion');
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Link to="/" className="login-back-home-link">← Retour à l&apos;accueil</Link>
      <div className="login-card">
        <div className="logo-container">
          <LogoIcon className="logo" />
        </div>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </button>

            </div>
            <div className="password-links-row">
              <p className="forgot-password-link">
                <Link to="/forgot-password">Mot de passe oublié ?</Link>
              </p>
              <p className="verify-email-link">
                <Link to="/verify-email-send">Vérifier mon email</Link>
              </p>
            </div>
          </div>
          <Button
            type="submit"
            variant="primary"
            value={isLoading ? "Connexion en cours..." : "Se connecter"}
            size="medium"
            disabled={isLoading}
          />
        </form>
        <div className="login-footer">
          <Button
            type="link"
            variant="secondary"
            value="S'inscrire"
            size="medium"
            linkHref='/register'
            className='register-button'
            targetBlank={false}
          />
          {/* <p className="register-link">
            Vous n'avez pas de compte ? <Link to="/register">Faire une demande d'inscription</Link>
          </p> */}


        </div>
      </div>
    </div>
  );
};

export default Login;